import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, MessageCircle, Clock, CheckCircle2, PartyPopper, XCircle, CalendarDays, MapPin, Stethoscope } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { useLang } from "../context/LanguageContext";
import { api, waLink } from "../lib/api";

const statusMeta = {
  pending: { icon: Clock, badge: "bg-amber-100 text-amber-700", ring: "bg-amber-50 text-amber-500" },
  confirmed: { icon: CheckCircle2, badge: "bg-blue-100 text-blue-700", ring: "bg-blue-50 text-blue-600" },
  completed: { icon: PartyPopper, badge: "bg-emerald-100 text-emerald-700", ring: "bg-emerald-50 text-emerald-600" },
  cancelled: { icon: XCircle, badge: "bg-red-100 text-red-700", ring: "bg-red-50 text-red-500" },
};

export default function Track() {
  const { lang, t } = useLang();
  const [params] = useSearchParams();
  const [ref, setRef] = useState(params.get("ref") || "");
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const search = useCallback(async (value) => {
    if (!value.trim()) return;
    setLoading(true);
    setError("");
    setBooking(null);
    try {
      const { data } = await api.get(`/bookings/track/${encodeURIComponent(value.trim())}`);
      setBooking(data);
    } catch {
      setError(t("track.not_found"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const initial = params.get("ref");
    if (initial) search(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const meta = booking ? statusMeta[booking.status] || statusMeta.pending : null;
  const StatusIcon = meta?.icon;

  return (
    <div data-testid="track-page" className="max-w-2xl mx-auto px-4 sm:px-6 py-16 min-h-[60vh]">
      <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 mb-4">{t("track.title")}</h1>
      <p className="text-base text-slate-500 mb-10">{t("track.subtitle")}</p>

      <form onSubmit={(e) => { e.preventDefault(); search(ref); }} className="flex gap-3 mb-10">
        <Input data-testid="track-ref-input" placeholder={t("track.placeholder")} value={ref}
          onChange={(e) => setRef(e.target.value.toUpperCase())} className="h-12 rounded-full px-5 font-mono" />
        <Button type="submit" disabled={loading || !ref.trim()} data-testid="track-submit-btn" className="rounded-full h-12 px-7">
          <Search className="w-4 h-4 mr-2" /> {loading ? "..." : t("track.btn")}
        </Button>
      </form>

      {error && (
        <p data-testid="track-error" className="text-sm text-red-600 bg-red-50 rounded-xl px-5 py-4">{error}</p>
      )}

      {booking && (
        <div data-testid="track-result" className="bg-white rounded-2xl border border-slate-200 p-8 animate-fade-up">
          <div className="flex items-start gap-4 mb-8">
            <span className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${meta.ring}`}>
              <StatusIcon className="w-7 h-7" />
            </span>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-heading text-xl font-semibold text-primary">{booking.booking_ref}</span>
                <Badge data-testid="track-status-badge" className={`${meta.badge} capitalize`}>{booking.status}</Badge>
              </div>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{t(`track.status.${booking.status}`)}</p>
            </div>
          </div>
          <dl className="space-y-4 text-sm">
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <Stethoscope className="w-4 h-4 text-slate-400 shrink-0" />
              <dt className="text-slate-500 w-28">{t("book.service_label")}</dt>
              <dd className="font-medium text-slate-900">{booking.service_name}</dd>
            </div>
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <CalendarDays className="w-4 h-4 text-slate-400 shrink-0" />
              <dt className="text-slate-500 w-28">{t("book.datetime_label")}</dt>
              <dd className="font-medium text-slate-900">{booking.date} • {booking.time}</dd>
            </div>
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <Search className="w-4 h-4 text-slate-400 shrink-0" />
              <dt className="text-slate-500 w-28">{t("book.doctor_label")}</dt>
              <dd className="font-medium text-slate-900">{booking.doctor_name || t("book.any_doctor")}</dd>
            </div>
            <div className="flex items-center gap-3">
              <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
              <dt className="text-slate-500 w-28">{t("book.area")}</dt>
              <dd className="font-medium text-slate-900">{booking.area}</dd>
            </div>
          </dl>
          <a href={waLink(lang === "ms"
              ? `Hai, saya ingin bertanya tentang tempahan saya (Rujukan: ${booking.booking_ref}).`
              : `Hi, I have a question about my booking (Ref: ${booking.booking_ref}).`)}
            target="_blank" rel="noopener noreferrer" data-testid="track-whatsapp-btn"
            className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#128C4A] hover:text-[#0d6b38] transition-colors">
            <MessageCircle className="w-4 h-4" /> {t("track.help")}
          </a>
        </div>
      )}
    </div>
  );
}
